<template>
  <HrPageLayout
    title="Payroll Setup Management"
    subtitle="Setup Management"
    :breadcrumb-items="[
      { label: 'Dashboard', route: '/dashboard' },
      { label: 'System Management' },
      { label: 'Setup & Configuration' }
    ]"
  >
    <div class="payroll-setup-content">
      <!-- Setup categories -->
      <div class="hr-content-grid hr-mb-4">
        <!-- Pay Grades -->
        <div class="hr-chart-card">
          <div class="hr-chart-header">
            <h5 class="hr-chart-title">
              <i class="bx bx-certification"></i> Pay Grades
            </h5>
            <button type="button" @click="showPayGradesModal" class="hr-btn hr-btn-sm hr-btn-primary">
              <i class="bx bx-cog"></i> Manage
            </button>
          </div>
          <div class="hr-chart-body">
            <div class="hr-text-center hr-py-3">
              <i class="bx bx-certification hr-text-primary" style="font-size: 48px;"></i>
              <h6 class="hr-mt-2 hr-mb-1">Pay Grade Management</h6>
              <p class="hr-text-muted hr-mb-0">
                Configure salary ranges and pay scales for different job grades.
              </p>
            </div>
            <ul class="hr-list-bullets">
              <li>Define minimum and maximum salary ranges</li>
              <li>Set midpoint salary values</li>
              <li>Link grades to salary structures</li>
            </ul>
            <div class="hr-text-center hr-mt-3">
              <button type="button" @click="showPayGradesModal" class="hr-btn hr-btn-sm hr-btn-outline-primary">
                <i class="bx bx-plus"></i> Configure Pay Grades
              </button>
            </div>
          </div>
        </div>

        <!-- Allowance Types -->
        <div class="hr-chart-card">
          <div class="hr-chart-header">
            <h5 class="hr-chart-title">
              <i class="bx bx-plus-circle"></i> Allowance Types
            </h5>
            <button type="button" @click="showAllowanceTypesModal" class="hr-btn hr-btn-sm hr-btn-primary">
              <i class="bx bx-cog"></i> Manage
            </button>
          </div>
          <div class="hr-chart-body">
            <div class="hr-text-center hr-py-3">
              <i class="bx bx-money hr-text-success" style="font-size: 48px;"></i>
              <h6 class="hr-mt-2 hr-mb-1">Allowance Type Management</h6>
              <p class="hr-text-muted hr-mb-0">
                Setup different types of allowances for employees.
              </p>
            </div>
            <ul class="hr-list-bullets">
              <li>Configure fixed amount allowances</li>
              <li>Set percentage-based allowances</li>
              <li>Define taxable status of allowances</li>
            </ul>
            <div class="hr-text-center hr-mt-3">
              <button type="button" @click="showAllowanceTypesModal" class="hr-btn hr-btn-sm hr-btn-outline-primary">
                <i class="bx bx-plus"></i> Configure Allowances
              </button>
            </div>
          </div>
        </div>

        <!-- Deduction Types -->
        <div class="hr-chart-card">
          <div class="hr-chart-header">
            <h5 class="hr-chart-title">
              <i class="bx bx-minus-circle"></i> Deduction Types
            </h5>
            <button type="button" @click="showDeductionTypesModal" class="hr-btn hr-btn-sm hr-btn-primary">
              <i class="bx bx-cog"></i> Manage
            </button>
          </div>
          <div class="hr-chart-body">
            <div class="hr-text-center hr-py-3">
              <i class="bx bx-money-withdraw hr-text-danger" style="font-size: 48px;"></i>
              <h6 class="hr-mt-2 hr-mb-1">Deduction Type Management</h6>
              <p class="hr-text-muted hr-mb-0">
                Setup different types of deductions for employees.
              </p>
            </div>
            <ul class="hr-list-bullets">
              <li>Configure fixed amount deductions</li>
              <li>Set percentage-based deductions</li>
              <li>Define pretax and mandatory status</li>
            </ul>
            <div class="hr-text-center hr-mt-3">
              <button type="button" @click="showDeductionTypesModal" class="hr-btn hr-btn-sm hr-btn-outline-primary">
                <i class="bx bx-plus"></i> Configure Deductions
              </button>
            </div>
          </div>
        </div>

        <!-- Tax Brackets -->
        <div class="hr-chart-card">
          <div class="hr-chart-header">
            <h5 class="hr-chart-title">
              <i class="bx bx-bar-chart"></i> Tax Brackets
            </h5>
            <button type="button" @click="showTaxBracketsModal" class="hr-btn hr-btn-sm hr-btn-primary">
              <i class="bx bx-cog"></i> Manage
            </button>
          </div>
          <div class="hr-chart-body">
            <div class="hr-text-center hr-py-3">
              <i class="bx bx-line-chart hr-text-info" style="font-size: 48px;"></i>
              <h6 class="hr-mt-2 hr-mb-1">Tax Bracket Management</h6>
              <p class="hr-text-muted hr-mb-0">
                Configure tax bracket structure for income tax calculations.
              </p>
            </div>
            <ul class="hr-list-bullets">
              <li>Define income ranges for taxation</li>
              <li>Set tax rates for each bracket</li>
              <li>Manage tax relief and exemptions</li>
            </ul>
            <div class="hr-text-center hr-mt-3">
              <button type="button" @click="showTaxBracketsModal" class="hr-btn hr-btn-sm hr-btn-outline-primary">
                <i class="bx bx-plus"></i> Configure Tax Brackets
              </button>
            </div>
          </div>
        </div>

        <!-- Statutory Deductions -->
        <div class="hr-chart-card">
          <div class="hr-chart-header">
            <h5 class="hr-chart-title">
              <i class="bx bx-gavel"></i> Statutory Deductions
            </h5>
            <button type="button" @click="showStatutoryDeductionsModal" class="hr-btn hr-btn-sm hr-btn-primary">
              <i class="bx bx-cog"></i> Manage
            </button>
          </div>
          <div class="hr-chart-body">
            <div class="hr-text-center hr-py-3">
              <i class="bx bx-shield-quarter hr-text-warning" style="font-size: 48px;"></i>
              <h6 class="hr-mt-2 hr-mb-1">Statutory Deduction Management</h6>
              <p class="hr-text-muted hr-mb-0">
                Configure mandatory statutory deductions like NSSF, NHIF, etc.
              </p>
            </div>
            <ul class="hr-list-bullets">
              <li>Setup tiered calculation methods</li>
              <li>Configure percentage-based deductions</li>
              <li>Manage fixed amount contributions</li>
            </ul>
            <div class="hr-text-center hr-mt-3">
              <button type="button" @click="showStatutoryDeductionsModal" class="hr-btn hr-btn-sm hr-btn-outline-primary">
                <i class="bx bx-plus"></i> Configure Statutory Deductions
              </button>
            </div>
          </div>
        </div>

        <!-- Salary Structure -->
        <div class="hr-chart-card">
          <div class="hr-chart-header">
            <h5 class="hr-chart-title">
              <i class="bx bx-grid-alt"></i> Salary Structures
            </h5>
            <button type="button" @click="showSalaryStructuresModal" class="hr-btn hr-btn-sm hr-btn-primary">
              <i class="bx bx-cog"></i> Manage
            </button>
          </div>
          <div class="hr-chart-body">
            <div class="hr-text-center hr-py-3">
              <i class="bx bx-layout hr-text-primary" style="font-size: 48px;"></i>
              <h6 class="hr-mt-2 hr-mb-1">Salary Structure Management</h6>
              <p class="hr-text-muted hr-mb-0">
                Define salary structures and components for the organization.
              </p>
            </div>
            <ul class="hr-list-bullets">
              <li>Create different salary structures</li>
              <li>Link structures with departments</li>
              <li>Setup default benefits package</li>
            </ul>
            <div class="hr-text-center hr-mt-3">
              <button type="button" @click="showSalaryStructuresModal" class="hr-btn hr-btn-sm hr-btn-outline-primary">
                <i class="bx bx-plus"></i> Configure Salary Structures
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Configuration overview chart & stats -->
      <div class="hr-chart-card">
        <div class="hr-chart-header">
          <h5 class="hr-chart-title">Payroll Configuration Overview</h5>
        </div>
        <div class="hr-chart-body">
          <div class="hr-content-grid" style="grid-template-columns: 2fr 1fr;">
            <div id="payroll-configuration-chart" style="height: 350px;">
              <!-- Chart can be rendered here if enabled -->
            </div>
            <div class="configuration-stats">
              <div class="config-stat-row">
                <div class="config-icon config-icon-primary">
                  <i class="bx bx-certification"></i>
                </div>
                <div class="config-label">Pay Grades</div>
                <div class="config-value">
                  <span class="hr-badge hr-badge-light-primary">{{ payGradesCount }}</span>
                </div>
              </div>
              <div class="config-stat-row">
                <div class="config-icon config-icon-success">
                  <i class="bx bx-money"></i>
                </div>
                <div class="config-label">Allowance Types</div>
                <div class="config-value">
                  <span class="hr-badge hr-badge-light-success">{{ allowanceTypesCount }}</span>
                </div>
              </div>
              <div class="config-stat-row">
                <div class="config-icon config-icon-danger">
                  <i class="bx bx-money-withdraw"></i>
                </div>
                <div class="config-label">Deduction Types</div>
                <div class="config-value">
                  <span class="hr-badge hr-badge-light-danger">{{ deductionTypesCount }}</span>
                </div>
              </div>
              <div class="config-stat-row">
                <div class="config-icon config-icon-info">
                  <i class="bx bx-line-chart"></i>
                </div>
                <div class="config-label">Tax Brackets</div>
                <div class="config-value">
                  <span class="hr-badge hr-badge-light-info">{{ taxBracketsCount }}</span>
                </div>
              </div>
              <div class="config-stat-row">
                <div class="config-icon config-icon-warning">
                  <i class="bx bx-gavel"></i>
                </div>
                <div class="config-label">Statutory Deductions</div>
                <div class="config-value">
                  <span class="hr-badge hr-badge-light-warning">{{ statutoryDeductionsCount }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Modals -->
      <PayGradesManagement ref="payGradesManagement" />
      <AllowanceTypesManagementComplete ref="allowanceTypesManagement" />
      <DeductionTypesManagement ref="deductionTypesManagement" />
      <TaxBracketManagement ref="taxBracketManagement" />
      <StatutoryDeductionManagement ref="statutoryDeductionManagement" />
      <SalaryStructureManagement ref="salaryStructureManagement" />
    </div>
  </HrPageLayout>
</template>

<script>
/* eslint-disable */
import { authUrl, execute } from "@/api";
import * as bootstrap from 'bootstrap';
import PayGradesManagement from "@/components/payroll/PayGradesManagement.vue";
import AllowanceTypesManagementComplete from "@/components/payroll/AllowanceTypesManagement.vue";
import DeductionTypesManagement from "@/components/payroll/DeductionTypesManagement.vue";
import TaxBracketManagement from "@/components/payroll/TaxBracketManagement.vue";
import StatutoryDeductionManagement from "@/components/payroll/StatutoryDeductionManagement.vue";
import SalaryStructureManagement from "@/components/payroll/SalaryStructureManagement.vue";
import HrPageLayout from "@/components/hrActions/HrPageLayout.vue";

// Import chart library if needed
// import Chart from 'chart.js/auto';

export default {
  name: 'PayrollSetupManagement',
  components: {
    HrPageLayout,
    PayGradesManagement,
    AllowanceTypesManagementComplete,
    DeductionTypesManagement,
    TaxBracketManagement,
    StatutoryDeductionManagement,
    SalaryStructureManagement
  },
  data() {
    return {
      loading: false,
      payGradesCount: 0,
      allowanceTypesCount: 0,
      deductionTypesCount: 0,
      taxBracketsCount: 0,
      statutoryDeductionsCount: 0,
      salaryStructuresCount: 0,
      // Chart data can be added here if needed
      chartData: null
    };
  },
  mounted() {
    this.getPayrollSetupCounts();
    // Initialize chart if needed
    // this.initChart();
  },
  methods: {
    getPayrollSetupCounts() {
      this.loading = true;

      const data = new FormData();
      data.append("function", "getPayrollSetupCounts");

      execute(data, authUrl)
          .then((res) => {
            this.loading = false;
            const success = res?.data?.success;
            const payload = res?.data?.data;
            if (success && payload) {
              this.payGradesCount = payload.payGradesCount ?? 0;
              this.allowanceTypesCount = payload.allowanceTypesCount ?? 0;
              this.deductionTypesCount = payload.deductionTypesCount ?? 0;
              this.taxBracketsCount = payload.taxBracketsCount ?? 0;
              this.statutoryDeductionsCount = payload.statutoryDeductionsCount ?? 0;
              this.salaryStructuresCount = payload.salaryStructuresCount ?? 0;
            } else if (!success) {
              console.error("Error fetching payroll setup counts:", res?.data?.message ?? "Unknown error");
            }
          })
          .catch((error) => {
            this.loading = false;
            console.error("API Error:", error);
          });
    },
    showPayGradesModal() {
      this.$nextTick(() => this.$refs.payGradesManagement?.showModal?.());
    },
    showAllowanceTypesModal() {
      this.$nextTick(() => this.$refs.allowanceTypesManagement?.showModal?.());
    },
    showDeductionTypesModal() {
      this.$nextTick(() => this.$refs.deductionTypesManagement?.showModal?.());
    },
    showTaxBracketsModal() {
      this.$nextTick(() => this.$refs.taxBracketManagement?.showModal?.());
    },
    showStatutoryDeductionsModal() {
      this.$nextTick(() => this.$refs.statutoryDeductionManagement?.showModal?.());
    },
    showSalaryStructuresModal() {
      this.$nextTick(() => this.$refs.salaryStructureManagement?.showModal?.());
    },
    /*
    // You can uncomment and implement chart functionality if needed
    initChart() {
      // Initialize chart with placeholder data
      const ctx = document.getElementById('payroll-configuration-chart');
      if (ctx) {
        this.chart = new Chart(ctx, {
          type: 'bar',
          data: {
            labels: ['Pay Grades', 'Allowances', 'Deductions', 'Tax Brackets', 'Statutory Deductions'],
            datasets: [{
              label: 'Configuration Count',
              data: [
                this.payGradesCount,
                this.allowanceTypesCount,
                this.deductionTypesCount,
                this.taxBracketsCount,
                this.statutoryDeductionsCount
              ],
              backgroundColor: [
                'rgba(78, 115, 223, 0.2)',
                'rgba(40, 167, 69, 0.2)',
                'rgba(220, 53, 69, 0.2)',
                'rgba(54, 185, 204, 0.2)',
                'rgba(246, 194, 62, 0.2)'
              ],
              borderColor: [
                'rgba(78, 115, 223, 1)',
                'rgba(40, 167, 69, 1)',
                'rgba(220, 53, 69, 1)',
                'rgba(54, 185, 204, 1)',
                'rgba(246, 194, 62, 1)'
              ],
              borderWidth: 1
            }]
          },
          options: {
            responsive: true,
            maintainAspectRatio: false,
            scales: {
              y: {
                beginAtZero: true,
                precision: 0
              }
            }
          }
        });
      }
    },
    updateChart(counts) {
      if (this.chart) {
        this.chart.data.datasets[0].data = [
          counts.payGradesCount || 0,
          counts.allowanceTypesCount || 0,
          counts.deductionTypesCount || 0,
          counts.taxBracketsCount || 0,
          counts.statutoryDeductionsCount || 0
        ];
        this.chart.update();
      }
    }
    */
  }
};
</script>

<style scoped>
@import "@/assets/css/hr-page-shared.css";

.hr-mb-4 {
  margin-bottom: 1.5rem;
}

.hr-text-center {
  text-align: center;
}

.hr-py-3 {
  padding-top: 1rem;
  padding-bottom: 1rem;
}

.hr-mt-2 {
  margin-top: 0.5rem;
}

.hr-mt-3 {
  margin-top: 1rem;
}

.hr-list-bullets {
  list-style: none;
  padding-left: 0;
  margin: 0;
}

.hr-list-bullets li {
  position: relative;
  padding-left: 1.5rem;
  margin-bottom: 0.35rem;
  font-size: 0.9rem;
  color: #4b5563;
}

.hr-list-bullets li::before {
  content: "•";
  position: absolute;
  left: 0;
  top: 0;
  color: #22c55e;
}

.configuration-stats {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.config-stat-row {
  display: grid;
  grid-template-columns: auto 1fr auto;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  border-radius: 10px;
  border: 1px solid #e5e7eb;
  background: #f9fafb;
  transition: background 0.2s, transform 0.2s, box-shadow 0.2s;
}

.config-stat-row:hover {
  background: #ffffff;
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(15, 23, 42, 0.08);
}

.config-icon {
  width: 34px;
  height: 34px;
  border-radius: 999px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #ffffff;
  font-size: 18px;
}

.config-icon-primary {
  background: linear-gradient(135deg, #3b82f6, #6366f1);
}

.config-icon-success {
  background: linear-gradient(135deg, #10b981, #22c55e);
}

.config-icon-danger {
  background: linear-gradient(135deg, #ef4444, #f97316);
}

.config-icon-info {
  background: linear-gradient(135deg, #06b6d4, #3b82f6);
}

.config-icon-warning {
  background: linear-gradient(135deg, #f59e0b, #eab308);
}

.config-label {
  font-size: 0.9rem;
  font-weight: 600;
  color: #374151;
}

.config-value {
  font-size: 0.85rem;
}
</style>

<!-- Ensure teleported modals and their backdrop appear above the nav (z-index 1000) -->
<style>
.payroll-setup-modal {
  z-index: 1055 !important;
}
.payroll-setup-modal.show {
  z-index: 1055 !important;
}
body .modal-backdrop {
  z-index: 1050 !important;
}
</style>